import type { Activity, ActivityRecord } from '../model/activity.js'
import {
  classifyInterval,
  MOVING_SPEED_THRESHOLD_MPS,
  overlapsStoppedWindow,
  readSpeedMetersPerSecond,
  timerStoppedWindows,
  timestampedRecordsInOrder,
} from './moving.js'
import { recalculated, type SummaryValue, unavailable } from './provenance.js'

/**
 * Average cadence in the unit users expect for the sport.
 *
 * - FIT record `cadence` for foot sports is *strides* (one foot) per
 *   minute, with optional `fractionalCadence` (1/128 resolution, already
 *   scaled to a fraction by the SDK). Users expect steps per minute, so
 *   for `STEP_SPORTS` each sample is `(cadence + fractionalCadence) * 2`
 *   and the unit is `'spm'`.
 * - Every other sport (cycling etc.) and an activity with no sport at all
 *   reports the raw value as `'rpm'` (crank revolutions per minute; the
 *   FIT field's native unit). Fractional cadence is added here too when
 *   present.
 * - Sport comes from the sessions' `sport`, falling back to the first
 *   `sport` message. If sessions span both unit families (e.g. a
 *   run+bike multisport file) a single average would mix units and is
 *   meaningless, so the result is `unavailable`.
 * - Only samples where cadence > 0 (and not the uint8 invalid sentinel
 *   255) count, and only on records where the athlete is moving: a
 *   record whose interval (see `calc/moving.ts` `classifyInterval`) is
 *   classified stopped, or which falls inside a timer-stopped window, is
 *   excluded — zeros/low values while standing would drag the average
 *   down. A record with no speed/distance evidence either way is kept.
 * - Rounded to the nearest whole rpm/spm. Tagged `recalculated`: it's a
 *   resummary of device-measured per-record cadence, like heart rate.
 */

export type CadenceUnit = 'rpm' | 'spm'

/** Sports whose FIT cadence is strides/min, displayed as steps/min. */
const STEP_SPORTS = new Set(['running', 'walking', 'hiking'])

function sportOf(activity: Activity): CadenceUnit | 'mixed' {
  const sports = activity.sessions
    .map((s) => s.sport)
    .filter((s): s is string => typeof s === 'string')
  if (sports.length === 0) {
    const fallback = activity.otherMessages.sportMesgs?.[0]?.sport
    if (typeof fallback === 'string') sports.push(fallback)
  }
  const units = new Set(sports.map((s) => (STEP_SPORTS.has(s) ? 'spm' : 'rpm')))
  if (units.size > 1) return 'mixed'
  return units.has('spm') ? 'spm' : 'rpm'
}

function cadenceSample(r: ActivityRecord): number | undefined {
  const c = r.cadence
  if (typeof c !== 'number' || !Number.isFinite(c) || c <= 0 || c >= 255) return undefined
  const frac = r.fractionalCadence
  return c + (typeof frac === 'number' && Number.isFinite(frac) && frac > 0 ? frac : 0)
}

export function computeAverageCadence(
  activity: Activity,
): SummaryValue<{ value: number; unit: CadenceUnit }> {
  const unit = sportOf(activity)
  if (unit === 'mixed') return unavailable()
  const records = timestampedRecordsInOrder(activity.records)
  const stopped = timerStoppedWindows(activity.events)

  let sum = 0
  let count = 0
  for (let i = 0; i < records.length; i++) {
    const r = records[i] as ActivityRecord & { timestamp: Date }
    const sample = cadenceSample(r)
    if (sample == null) continue
    const t = r.timestamp.getTime()
    // Strictly inside a timer-stopped window (the records at the stop/start instants themselves belong to running time).
    if (stopped.some((w) => t > w.start && t < w.end)) continue
    // The interval ending here decides, unless there is none (first
    // record) or it spans a timer pause — then the record's own
    // instantaneous speed is the only relevant evidence.
    const prev = i > 0 ? (records[i - 1] as ActivityRecord & { timestamp: Date }) : undefined
    let moving: boolean | undefined
    if (prev != null && !overlapsStoppedWindow(prev.timestamp.getTime(), t, stopped)) {
      moving = classifyInterval(prev, r)
    } else {
      const speed = readSpeedMetersPerSecond(r)
      moving = speed == null ? undefined : speed >= MOVING_SPEED_THRESHOLD_MPS
    }
    if (moving === false) continue
    sum += sample
    count++
  }
  if (count === 0) return unavailable()
  const avg = sum / count
  return recalculated({ value: Math.round(unit === 'spm' ? avg * 2 : avg), unit })
}
